import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Stack,
  Typography,
  CircularProgress,
  FormControlLabel,
  Switch
} from "@mui/material";

import { useTesterIdentity } from "./TesterIdentityProvider";
import { reportBug } from "./api";
import { looksLikeAgentTranscript } from "./sanitize";

const SEVERITY_OPTIONS = [
  { value: "blocker", label: "Blocker — flow is broken" },
  { value: "major", label: "Major — clear bug, affects usage" },
  { value: "minor", label: "Minor — works but wrong" },
  { value: "cosmetic", label: "Cosmetic — visual only" }
];

const SENSITIVE_KEY_PATTERN = /token|secret|password|apikey|api_key|auth/i;

const captureClientContext = () => {
  try {
    return Object.fromEntries(
      Object.entries(sessionStorage).filter(
        ([key]) =>
          !SENSITIVE_KEY_PATTERN.test(key) &&
          !key.startsWith("firebase:") &&
          !key.startsWith("__")
      )
    );
  } catch {
    return {};
  }
};

const ReportBugDialog = ({ open, onClose, onReported, hillId }) => {
  const { getIdToken } = useTesterIdentity();
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState("major");
  const [investigateFirst, setInvestigateFirst] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setDescription("");
    setSeverity("major");
    setInvestigateFirst(false);
    setError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) {
      return;
    }
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      setError("Description is required");
      return;
    }
    if (looksLikeAgentTranscript(description)) {
      setError(
        "This looks like a copied agent prompt or transcript. Please describe the bug in your own words — what you were doing and what went wrong."
      );
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await reportBug({
        description: description.trim(),
        severity,
        pageUrl: window.location.href,
        screenshotUrls: [],
        recorderSnapshot: [],
        clientContext: captureClientContext(),
        investigateFirst,
        hillId,
        getIdToken
      });
      reset();
      onReported?.(result.bugId);
      onClose();
    } catch (err) {
      setError(err.message || "Failed to report bug");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Report a bug</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="textSecondary">
            What did you see? Include what you were doing and what went wrong.
            The current URL ({window.location.pathname}) is attached
            automatically.
          </Typography>
          <TextField
            label="Description"
            multiline
            minRows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={submitting}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- intentional: focus the field the user is about to type into
            autoFocus
            fullWidth
          />
          <TextField
            select
            label="Severity"
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            disabled={submitting}
            fullWidth
          >
            {SEVERITY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Stack spacing={0.5}>
            <FormControlLabel
              control={
                <Switch
                  checked={investigateFirst}
                  onChange={(e) => setInvestigateFirst(e.target.checked)}
                  disabled={submitting}
                />
              }
              label="Investigate the code first before proposing a fix"
            />
            <Typography variant="caption" color="textSecondary">
              Forces a read-only code investigation (~2–3 min) before any fix
              plan. Use when you want the agent to ground its plan in the
              actual code rather than guess.
            </Typography>
          </Stack>
          {error ? (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || !description.trim()}
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {submitting ? "Reporting…" : "Report bug"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportBugDialog;
